// IndexedDB VRM Cache Manager
export class VRMCacheManager {
  private dbName = 'VRMCache';
  private version = 2; // Increment version to force schema update
  private storeName = 'vrm-files';

  async openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        console.log('🔄 IndexedDB upgrade needed:', {
          oldVersion: event.oldVersion,
          newVersion: event.newVersion,
          existingStores: Array.from(db.objectStoreNames)
        });

        // Clean up old stores if they exist
        if (db.objectStoreNames.contains(this.storeName)) {
          db.deleteObjectStore(this.storeName);
          console.log('🗑️ Deleted existing store for schema update');
        }

        // Create new store
        const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
        store.createIndex('timestamp', 'timestamp', { unique: false });

        console.log('✅ Created new VRM cache store with proper schema');
      };
    });
  }

  async getFromCache(key: string): Promise<ArrayBuffer | null> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.get(key);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          const result = request.result;
          if (result && result.data) {
            console.log(`📦 Cache hit for ${key}`);
            resolve(result.data);
          } else {
            console.log(`📦 Cache miss for ${key}`);
            resolve(null);
          }
        };
      });
    } catch (error) {
      console.warn('IndexedDB error:', error);
      return null;
    }
  }

  async saveToCache(key: string, data: ArrayBuffer): Promise<void> {
    try {
      if (!key || !data) {
        console.warn('Invalid key or data for caching:', { key: !!key, data: !!data });
        return;
      }

      // Validate that data is actually an ArrayBuffer
      if (!(data instanceof ArrayBuffer)) {
        console.warn('Data is not an ArrayBuffer:', typeof data, data);
        return;
      }

      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      const record = {
        key: String(key), // Ensure key is a string
        data,
        timestamp: Date.now()
      };

      console.log('🔍 Saving record:', {
        key: record.key,
        dataSize: data.byteLength,
        timestamp: record.timestamp,
        recordKeys: Object.keys(record)
      });

      return new Promise((resolve, reject) => {
        const request = store.put(record);
        request.onerror = () => {
          console.error('IndexedDB put error:', {
            error: request.error,
            errorName: request.error?.name,
            errorMessage: request.error?.message,
            record: { ...record, data: '[ArrayBuffer]' }
          });
          reject(request.error);
        };
        request.onsuccess = () => {
          console.log(`💾 Successfully cached ${key} (${(data.byteLength / 1024 / 1024).toFixed(2)}MB)`);
          resolve();
        };
      });
    } catch (error) {
      console.error('Failed to cache VRM - outer catch:', {
        error,
        key,
        dataType: typeof data,
        dataIsArrayBuffer: data instanceof ArrayBuffer,
        dataSize: data instanceof ArrayBuffer ? data.byteLength : 'N/A'
      });
      throw error; // Re-throw to see the error in the calling code
    }
  }

  async clearCache(): Promise<void> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.clear();
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          console.log('🗑️ VRM Cache cleared');
          resolve();
        };
      });
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }

  async debugDatabase(): Promise<void> {
    try {
      const db = await this.openDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);

      console.log('🔍 Database Debug Info:', {
        dbName: this.dbName,
        version: this.version,
        storeName: this.storeName,
        keyPath: store.keyPath,
        autoIncrement: store.autoIncrement,
        indexNames: Array.from(store.indexNames)
      });

      // Try to count existing records
      const countRequest = store.count();
      countRequest.onsuccess = () => {
        console.log('📊 Records in cache:', countRequest.result);
      };

      // Try to get all keys
      const keysRequest = store.getAllKeys();
      keysRequest.onsuccess = () => {
        console.log('🔑 Cache keys:', keysRequest.result);
      };

    } catch (error) {
      console.error('Failed to debug database:', error);
    }
  }
}